import React from "react";
import { Navigation, Car, ExternalLink, MapPin, Map } from "lucide-react";

// Smart routing — mirrors the mobile app: Ryde GO for Grenada, recommended
// rideshare apps for other islands. `web` opens on desktop; `ios`/`android`
// open the app's download page on a mobile browser.
const RYDE_GO = {
  name: "Ryde GO",
  blurb: "Grenada's #1 rideshare",
  web: "https://rydego.gd/",
  ios: "https://apps.apple.com/us/app/ryde-go/id6749002766",
  android: "https://play.google.com/store/apps/details?id=ryde.go.customer",
};
const UBER = {
  name: "Uber",
  blurb: "Request a ride nearby",
  ios: "https://apps.apple.com/us/app/uber/id368677368",
  android: "https://play.google.com/store/apps/details?id=com.ubercab",
};
const INDRIVE = {
  name: "inDrive",
  blurb: "Popular island rideshare",
  ios: "https://apps.apple.com/us/app/indrive-cheaper-city-rides/id780125801",
  android: "https://play.google.com/store/apps/details?id=sinet.startup.inDriver",
};
const PICKUP = {
  name: "PickUp",
  blurb: "Barbados ride-hailing",
  ios: "https://apps.apple.com/us/app/pickup-barbados/id1478496556",
  android: "https://play.google.com/store/apps/details?id=com.pickup.rider",
};

const ISLAND_RIDES = {
  grenada: RYDE_GO,
  "puerto rico": UBER,
  jamaica: INDRIVE,
  barbados: PICKUP,
};

function pickProvider(island) {
  const key = (island || "").trim().toLowerCase();
  for (const [name, p] of Object.entries(ISLAND_RIDES)) {
    if (key.includes(name)) return p;
  }
  return UBER; // recommended default for other islands
}

function uberDeepLink(dropoff) {
  return `https://m.uber.com/ul/?action=setPickup&pickup=my_location&dropoff[formatted_address]=${encodeURIComponent(dropoff)}`;
}

export default function GetRide({ booking }) {
  const destination = [booking.meeting_point, booking.location || booking.island]
    .filter(Boolean).join(", ") || booking.island || "meeting point";
  const provider = pickProvider(booking.island);
  const mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(destination)}`;

  const handleRide = () => {
    const ua = navigator.userAgent || "";
    const isIOS = /iphone|ipad|ipod/i.test(ua);
    const isAndroid = /android/i.test(ua);
    let url;
    if (isIOS && provider.ios) url = provider.ios;          // mobile -> app download page
    else if (isAndroid && provider.android) url = provider.android;
    else url = provider.web || (provider === UBER ? uberDeepLink(destination) : mapsUrl); // desktop -> web ride link
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="glass neon-border rounded-2xl p-6" data-testid="get-a-ride">
      <div className="flex items-center gap-2 mb-1">
        <Navigation className="w-4 h-4 text-cyan-300" />
        <span className="font-mono-caption text-xs uppercase tracking-[0.2em] text-cyan-300">How to get here</span>
      </div>
      <h3 className="font-heading text-xl font-bold text-white">Getting to your meeting point</h3>
      <p className="text-sm text-slate-300 mt-2 flex items-start gap-2">
        <MapPin className="w-4 h-4 text-orange-400 mt-0.5 shrink-0" />
        <span>{destination}</span>
      </p>

      <div className="mt-4 flex items-center gap-3 rounded-xl bg-[#0B0E1B] border border-[#232A4D] px-4 py-3">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-fuchsia-600 flex items-center justify-center shrink-0">
          <Car className="w-5 h-5 text-white" />
        </div>
        <div className="min-w-0">
          <p className="text-sm text-white font-semibold">Recommended: {provider.name}</p>
          <p className="text-xs text-slate-400 truncate">{provider.blurb} · {booking.island}</p>
        </div>
      </div>

      <button
        onClick={handleRide}
        data-testid="find-ride-btn"
        className="mt-4 w-full btn-neon rounded-xl py-3.5 font-semibold flex items-center justify-center gap-2"
      >
        <Navigation className="w-4 h-4" /> Find a Ride to Meeting Point <ExternalLink className="w-4 h-4 opacity-80" />
      </button>
      <a
        href={mapsUrl}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="open-maps-link"
        className="mt-2 w-full glass rounded-xl py-2.5 font-medium text-sm text-slate-200 hover:text-cyan-300 flex items-center justify-center gap-2"
      >
        <Map className="w-4 h-4" /> Or open directions in Maps
      </a>
    </div>
  );
}
